import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { LocalOffer as LocalOfferIcon } from "../icons";
import { Event as EventIcon } from "../icons";
import { Storefront as StorefrontIcon } from "../icons";
import { DirectionsCar as DirectionsCarIcon } from "../icons";
import { Flight as FlightIcon } from "../icons";
import { Forum as ForumIcon } from "../icons";
import { ShowChart as ShowChartIcon } from "../icons";
import { collection, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { T } from "./Translate";
import PublicPageLayout, { useAuthUser } from "../components/PublicPageLayout";
import GoogleAdInline from "../components/GoogleAdInline";
import GuestLoginPrompt from "../components/GuestLoginPrompt";
import { formatEventDatesList } from "./eventsUtils";
import { brandColors } from "../theme";

// "Ver Notiline sin iniciar sesión" (/explore): lo último publicado en cada
// sección, con cada tarjeta enlazando a su ficha pública. Mirar es libre;
// usar cupones, comprar en tiendas, consultar o asistir pide cuenta (eso lo
// resuelve cada ficha). También es una página con contenido real para
// Google/AdSense.
const PER_SECTION = 6;

const excerpt = (text, max = 120) => (text && text.length > max ? `${text.slice(0, max).trimEnd()}…` : text);

// `toCard` puede devolver null para descartar un documento (p. ej. reposts);
// por eso algunas secciones piden más de PER_SECTION.
const money = (value) => (value ? `$ ${Number(value).toLocaleString()}` : "");

const SECTIONS = [
  {
    key: "offers",
    title: <T>Ofertas y cupones</T>,
    icon: LocalOfferIcon,
    load: () => query(collection(db, "offers"), orderBy("createdAt", "desc"), limit(PER_SECTION)),
    toCard: (d) => {
      const discount = Number(d.offer) || 0;
      return {
        to: `/offers/${d.id}`,
        image: d.images?.[0],
        title: d.product || d.title,
        detail: [discount ? `${discount}% OFF` : "", money(d.price)].filter(Boolean).join(" · "),
      };
    },
  },
  {
    key: "prices",
    title: <T>Precios que seguimos</T>,
    icon: ShowChartIcon,
    load: () => query(collection(db, "priceHistory"), orderBy("discount", "desc"), limit(PER_SECTION)),
    toCard: (d) => ({
      to: "/precios",
      key: d.id,
      image: d.image,
      title: d.name,
      detail: [d.discount ? `${d.discount}% OFF` : "", money(d.price)].filter(Boolean).join(" · "),
    }),
    moreTo: "/precios",
  },
  {
    key: "events",
    title: <T>Eventos</T>,
    icon: EventIcon,
    // Solo públicos: las reglas no dejan listar eventos privados.
    load: () =>
      query(collection(db, "events"), where("isPrivate", "==", false), orderBy("createdAt", "desc"), limit(PER_SECTION)),
    toCard: (d) => ({
      to: `/events/${d.id}`,
      image: d.images?.[0],
      title: d.title,
      detail: formatEventDatesList(d),
    }),
  },
  {
    key: "threads",
    title: <T>Hilos</T>,
    icon: ForumIcon,
    load: () => query(collection(db, "threads"), orderBy("createdAt", "desc"), limit(PER_SECTION * 3)),
    toCard: (d) =>
      d.repostOf || !(d.text || d.images?.length)
        ? null
        : {
            to: `/hilos/${d.id}`,
            image: d.images?.[0],
            title: excerpt(d.text) || d.name,
            detail: d.name,
          },
  },
  {
    key: "stores",
    title: <T>Tiendas</T>,
    icon: StorefrontIcon,
    load: () => query(collection(db, "stores"), limit(PER_SECTION * 3)),
    // Tiendas sin nombre (incompletas o de prueba) no se muestran.
    toCard: (d) =>
      !d.storeName
        ? null
        : {
            to: `/stores/${d.id}`,
            image: d.bannerUrl || d.logoUrl,
            title: d.storeName,
            detail: d.address,
          },
    moreTo: "/store-directory",
  },
  {
    key: "cars",
    title: <T>Autos</T>,
    icon: DirectionsCarIcon,
    load: () => query(collection(db, "cars"), orderBy("createdAt", "desc"), limit(PER_SECTION)),
    toCard: (d) => ({
      to: `/cars/${d.id}`,
      image: d.images?.[0],
      title: d.title,
      detail: [money(d.price), d.city].filter(Boolean).join(" · "),
    }),
  },
  {
    key: "trips",
    title: <T>Viajes y propiedades</T>,
    icon: FlightIcon,
    load: () => query(collection(db, "places"), orderBy("createdAt", "desc"), limit(PER_SECTION)),
    toCard: (d) => ({
      to: `/trips/${d.id}`,
      image: d.images?.[0],
      title: d.title,
      detail: [money(d.price), d.city].filter(Boolean).join(" · "),
    }),
  },
];

const ExploreCard = ({ card, Icon }) => (
  <Card sx={{ height: "100%", borderRadius: 3, backgroundColor: brandColors.backgroundPaper, border: `1px solid ${brandColors.divider}` }}>
    <CardActionArea component={RouterLink} to={card.to} sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}>
      {card.image ? (
        <Box component="img" src={card.image} alt={card.title || ""} loading="lazy" sx={{ width: "100%", height: 140, objectFit: "cover", display: "block" }} />
      ) : (
        <Box sx={{ height: 140, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: brandColors.backgroundDefault }}>
          <Icon sx={{ fontSize: 40, color: "text.disabled" }} />
        </Box>
      )}
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>{card.title}</Typography>
        {card.detail && (
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            {card.detail}
          </Typography>
        )}
      </CardContent>
    </CardActionArea>
  </Card>
);

const Explore = () => {
  const { checked, user } = useAuthUser();
  const [items, setItems] = useState(null);

  useEffect(() => {
    let active = true;
    Promise.all(
      SECTIONS.map((s) =>
        getDocs(s.load())
          .then((snap) =>
            snap.docs
              .map((doc) => s.toCard({ id: doc.id, ...doc.data() }))
              .filter(Boolean)
              .slice(0, PER_SECTION)
          )
          .catch((e) => {
            console.error(`Error cargando ${s.key}:`, e);
            return [];
          })
      )
    ).then((lists) => {
      if (!active) return;
      setItems(Object.fromEntries(SECTIONS.map((s, i) => [s.key, lists[i]])));
    });
    return () => {
      active = false;
    };
  }, []);

  const loading = !checked || !items;
  const hasContent = Boolean(items) && SECTIONS.some((s) => items[s.key].length > 0);

  return (
    <PublicPageLayout
      title={<T>Explorar Notiline</T>}
      loading={loading}
      notFound={!hasContent && <T>Todavía no hay publicaciones.</T>}
      isGuest={!user}
      showAd
      inlineAd={false}
    >
      <Stack spacing={4}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            <T>Lo último en Notiline</T>
          </Typography>
          <Typography sx={{ color: "text.secondary", mt: 1 }}>
            <T>Mirá ofertas, eventos, hilos, tiendas, autos y viajes sin iniciar sesión. Para usar cupones, comprar en tiendas o contactar a un vendedor, creá tu cuenta gratis.</T>
          </Typography>
        </Box>

        {SECTIONS.filter((s) => items?.[s.key]?.length > 0).map((s, i) => (
          <React.Fragment key={s.key}>
            <Box component="section">
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <s.icon sx={{ color: brandColors.accent }} />
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
                    {s.title}
                  </Typography>
                </Stack>
                {s.moreTo && (
                  <Button component={RouterLink} to={s.moreTo} size="small">
                    <T>Ver todas</T>
                  </Button>
                )}
              </Stack>
              <Grid container spacing={2}>
                {items[s.key].map((card) => (
                  <Grid item xs={12} sm={6} md={4} key={card.key || card.to}>
                    <ExploreCard card={card} Icon={s.icon} />
                  </Grid>
                ))}
              </Grid>
            </Box>
            {/* Anuncio después de la primera sección, si no entra el lateral */}
            {i === 0 && <GoogleAdInline sx={{ my: 0 }} />}
          </React.Fragment>
        ))}
      </Stack>
      {checked && !user && <GuestLoginPrompt />}
    </PublicPageLayout>
  );
};

export default Explore;
